import React, {useState} from 'react';
import {View, StyleSheet} from 'react-native';
import {useSelector} from 'react-redux';
import {redirectToProductDetail, removeItemFromBox} from '../../actions/scanpackScanningActions';
import OrderItem from '../../commons/orderItem';
import OrderItemImage from '../../commons/orderItemImage';
import BoxChampReopenPrompt from '../scanpack/BoxChampReopenPrompt';
import {reopenTarget} from '../scanpack/boxChampReopen';
import useMultiBoxState from './hooks/useMultiBoxState';
import MultiBoxHeader from './MultiBoxHeader';
import MultiBoxNavBar from './MultiBoxNavBar';
import ScrollableItemList from './ScrollableItemList';

const guidedBoxItems = (items, entry) => {
  const filled = {};
  const add = (key, r) => {
    const prev = filled[key];
    filled[key] = {qty: (prev ? prev.qty : 0) + r.filled, kitId: prev ? prev.kitId : r.row.kit_id ?? null};
  };
  entry.rows.forEach(r => {
    if (r.filled <= 0) return;
    add(`${r.row.order_item_id}_${r.row.product_id}`, r);
    add(`${r.row.order_item_id}`, r);
  });
  const listed = (items || []).flatMap(item => {
    const hit = filled[`${item.order_item_id}_${item.product_id}`];
    return hit ? [{...item, box_item_qty: hit.qty, box_kit_id: hit.kitId}] : [];
  });
  const byComponent = new Set(listed.map(item => item.order_item_id));
  const wholeKits = (items || []).filter(
    item =>
      item.child_items?.length &&
      !byComponent.has(item.order_item_id) &&
      filled[`${item.order_item_id}`] &&
      !filled[`${item.order_item_id}_${item.product_id}`]
  );
  return [...listed, ...wholeKits.map(item => ({...item, box_whole_kit: true}))];
};

const ScannedItemsList = () => {
  const {
    dispatch,
    currentOrder,
    multiBox,
    currentBoxId,
    boxName,
    showBoxUi,
    guidedView,
    guidedEntry
  } = useMultiBoxState();
  const sealConfirmedBoxIds = useSelector(
    state => state.scanPackScanning.boxChamp?.sealConfirmedBoxIds || []
  );
  const [reopen, setReopen] = useState(null);

  const items = currentOrder?.scanned_items;

  const onItemPress = (item) => {
    dispatch(redirectToProductDetail(item.product_id));
  };

  const getDisplayItems = () => {
    if (!items) return [];
    if (!multiBox) return items;
    if (guidedView) return guidedEntry ? guidedBoxItems(items, guidedEntry) : items;
    if (!currentBoxId) return [];

    const boxEntryMap = {};
    (currentOrder?.order_item_boxes || []).forEach(entry => {
      if (entry.box_id === currentBoxId) {
        const key = `${entry.order_item_id}_${entry.product_id || ''}`;
        boxEntryMap[key] = entry;
      }
    });

    return items
      .filter(item => {
        const key = `${item.order_item_id}_${item.product_id || ''}`;
        return boxEntryMap[key] !== undefined;
      })
      .map(item => {
        const key = `${item.order_item_id}_${item.product_id || ''}`;
        const entry = boxEntryMap[key];
        return { ...item, box_item_qty: entry.item_qty || 1, box_kit_id: entry.kit_id || null };
      });
  };

  const filteredItems = getDisplayItems();

  const removeFromBox = item => {
    const removal = () =>
      dispatch(removeItemFromBox(currentBoxId, item.order_item_id, item.product_id, item.box_kit_id));
    if (guidedView) {
      const sealed = reopenTarget({
        view: guidedView,
        orderItemId: item.order_item_id,
        kitId: item.box_kit_id,
        preferredBoxId: currentBoxId,
        sealConfirmedBoxIds
      });
      if (sealed) {
        setReopen({box: sealed, removal});
        return;
      }
    }
    removal();
  };

  const renderScannedItem = (item, index) => (
    <View
      key={`scanned-${item.product_id || item.item_id}-${index}`}
      style={styles.scannedItemWrapper}>
      <View style={styles.scannedItemContainer}>
        <OrderItem
          item={item}
          redirectToItemDetail={onItemPress}
          testID={'redirectToItem'}
        />
        <OrderItemImage
          item={item}
          listType={'scanned'}
          boxQty={showBoxUi ? item.box_item_qty : undefined}
          boxName={showBoxUi ? boxName : undefined}
          onRemoveFromBox={showBoxUi && !item.box_whole_kit ? () => removeFromBox(item) : undefined}
        />
      </View>
    </View>
  );

  const headerElement = (
    <>
      <BoxChampReopenPrompt
        box={reopen?.box || null}
        onConfirm={() => {
          const pending = reopen;
          setReopen(null);
          if (pending) pending.removal();
        }}
        onCancel={() => setReopen(null)}
      />
      <MultiBoxHeader />
      <MultiBoxNavBar />
    </>
  );

  return (
    <ScrollableItemList
      items={filteredItems}
      renderItem={renderScannedItem}
      emptyText={showBoxUi ? 'No items in this box' : 'No scanned items'}
      header={headerElement}
    />
  );
};

const styles = StyleSheet.create({
  scannedItemWrapper: {
    marginBottom: 8
  },
  scannedItemContainer: {
    flexDirection: 'row',
    borderRadius: 30
  }
});

export default ScannedItemsList;
